import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Dosya, Hizmet, IsEmriDetay, IsEmriDurumu, ParaBirimi, Urun } from '@nero/shared';
import {
  ANA_PARA_BIRIMI, ODEME_TIPI_ADI,
  DOSYA_TUR_ADI, IS_EMRI_DURUMLARI, IS_EMRI_DURUM_ADI, ODEME_TIPLERI,
  ARACSIZ, boyutGoster, para, plakaGoster, tarihSaatGoster,
} from '@nero/shared';
import { api, useEylem, useVeri } from '../lib/veri.js';
import { buyukDosyayiArsivle } from '../lib/arsiv.js';
import { useOturum } from '../lib/oturum.js';
import { useBildirim } from '../lib/bildirim.js';
import { HataKutusu, Kart, KartBaslik, Rozet, UyariSerit, Yukleniyor } from '../bilesenler/temel.js';
import { LogAnalizKarti } from './ortak/LogAnalizKarti.js';
import { Girdi, MetinAlani, ParaGirdi, Secim } from '../bilesenler/form.js';
import { OnayPenceresi, Pencere } from '../bilesenler/Pencere.js';
import { Simge } from '../bilesenler/Simge.js';
import { DurumRozeti, OdemeRozeti } from './ortak/rozetler.js';

export function IsEmriDetayi() {
  const { id } = useParams();
  const git = useNavigate();
  const bildir = useBildirim();
  const { yetkili } = useOturum();
  const { veri, yukleniyor, hata, yenile } = useVeri<IsEmriDetay>(id ? `/is-emirleri/${id}` : null);
  const [kalemAcik, setKalemAcik] = useState(false);
  const [odemeAcik, setOdemeAcik] = useState(false);
  const [duzenleAcik, setDuzenleAcik] = useState(false);
  const [silAcik, setSilAcik] = useState(false);

  const durumDegistir = useEylem(async (durum: IsEmriDurumu) => {
    await api.gonder(`/is-emirleri/${id}/durum`, { durum });
    yenile();
    bildir(`Durum «${IS_EMRI_DURUM_ADI[durum]}» olarak güncellendi`);
  });

  const sil = useEylem(async () => {
    await api.sil(`/is-emirleri/${id}`);
    bildir('İş emri çöpe taşındı');
    git('/is-emirleri');
  });

  if (hata) return <div className="sayfa"><HataKutusu hata={hata} yenile={yenile} /></div>;
  if (yukleniyor || !veri) return <div className="sayfa"><Yukleniyor satir={6} /></div>;

  const kalan = Math.max(0, veri.tutar - veri.tahsil_edilen);
  const duzenlenebilir = yetkili('is_emri.duzenle') && veri.durum !== 'Iptal';

  return (
    <div className="sayfa">
      <div className="sayfa-baslik">
        <div>
          <Link to="/is-emirleri" className="btn btn-sade btn-kucuk" style={{ marginBottom: 6, marginLeft: -8 }}>
            <Simge ad="ok-sol" boyut={14} /> İş emirleri
          </Link>
          <h1>{plakaGoster(veri.plaka) || ARACSIZ} <span style={{ color: 'var(--metin-3)', fontWeight: 500 }}>· {veri.no}</span></h1>
          <div className="alt">
            {[veri.marka, veri.model, veri.motor].filter(Boolean).join(' · ')} · {veri.musteri_ad}
          </div>
        </div>
        <div className="eylemler">
          {yetkili('proforma.duzenle') && veri.durum !== 'Iptal' && (
            <Link className="btn btn-sade" to={`/proforma/yeni?is_emri=${veri.id}`}>
              <Simge ad="dosya" className="simge" /> Proforma
            </Link>
          )}
          {duzenlenebilir && (
            <button className="btn btn-ikincil" onClick={() => setDuzenleAcik(true)}>
              <Simge ad="kalem" className="simge" /> Düzenle
            </button>
          )}
          {yetkili('odeme.al') && kalan > 0 && veri.durum !== 'Iptal' && (
            <button className="btn btn-birincil" onClick={() => setOdemeAcik(true)}>
              <Simge ad="kasa" className="simge" /> Ödeme al
            </button>
          )}
        </div>
      </div>

      <div className="satir" style={{ gap: 'var(--b2)', marginBottom: 'var(--b5)', flexWrap: 'wrap' }}>
        <DurumRozeti durum={veri.durum} />
        <OdemeRozeti durum={veri.odeme_durumu} />
        {duzenlenebilir && IS_EMRI_DURUMLARI.filter((d) => d !== veri.durum).map((d) => (
          <button
            key={d}
            className="btn btn-sade btn-kucuk"
            disabled={durumDegistir.calisiyor}
            onClick={() => void durumDegistir.yurut(d)}
          >
            → {IS_EMRI_DURUM_ADI[d]}
          </button>
        ))}
      </div>

      {durumDegistir.hata && <UyariSerit ton="kritik">{durumDegistir.hata}</UyariSerit>}

      <div className="izgara izgara-yan">
        <div className="sutun" style={{ gap: 'var(--b4)' }}>
          <Kart>
            <KartBaslik baslik="Kalemler">
              {duzenlenebilir && (
                <button className="btn btn-ikincil btn-kucuk" onClick={() => setKalemAcik(true)}>
                  <Simge ad="arti" boyut={14} /> Kalem ekle
                </button>
              )}
            </KartBaslik>
            {veri.kalemler.length === 0 ? (
              <div className="kart-govde" style={{ color: 'var(--metin-3)', fontSize: 'var(--p-13)' }}>
                Henüz kalem eklenmedi. Tutar elle girilmiş: {para(veri.tutar)}
              </div>
            ) : (
              <div className="tablo-sarma">
                <table className="tablo">
                  <thead>
                    <tr><th>Açıklama</th><th className="sag">Adet</th><th className="sag">Birim</th><th className="sag">Tutar</th><th /></tr>
                  </thead>
                  <tbody>
                    {veri.kalemler.map((k) => (
                      <tr key={k.id}>
                        <td>
                          <span className="ana">{k.aciklama}</span>
                          {k.tur === 'urun' && <Rozet ton="notr" sade>Stok</Rozet>}
                        </td>
                        <td className="sag sayi">{k.adet}</td>
                        {/* Dövizli kalemde önce YAZILAN fiyat, altında TL
                            karşılığı. TL kalemlerde ikinci satır çıkmaz. */}
                        <td className="sag para ikincil">
                          {para(k.doviz_birim_fiyat ?? k.birim_fiyat, k.para_birimi)}
                          {(k.para_birimi ?? ANA_PARA_BIRIMI) !== ANA_PARA_BIRIMI && (
                            <div className="doviz-alt">{para(k.birim_fiyat)}</div>
                          )}
                        </td>
                        <td className="sag para">{para(k.tutar)}</td>
                        <td className="dar">
                          {duzenlenebilir && (
                            <KalemSil isEmriId={veri.id} kalemId={k.id} yenile={yenile} />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Kart>

          <Kart>
            <KartBaslik baslik="Ödemeler" />
            {veri.odemeler.length === 0 ? (
              <div className="kart-govde" style={{ color: 'var(--metin-3)', fontSize: 'var(--p-13)' }}>
                Henüz ödeme alınmadı.
              </div>
            ) : (
              <div className="tablo-sarma">
                <table className="tablo">
                  <thead>
                    <tr>
                      <th>Tarih</th><th>Yöntem</th><th>Not</th><th className="sag">Tutar</th>
                      {yetkili('kasa.sil') && <th className="dar" />}
                    </tr>
                  </thead>
                  <tbody>
                    {veri.odemeler.map((o) => (
                      <tr key={o.id}>
                        <td className="ikincil dar">{o.tarih} {o.saat}</td>
                        <td>{ODEME_TIPI_ADI[o.odeme_tipi] ?? o.odeme_tipi}</td>
                        <td className="ikincil">{o.not || '—'}</td>
                        <td className="sag para">{para(o.tutar)}</td>
                        {yetkili('kasa.sil') && (
                          <td className="sag">
                            <OdemeSil isEmriId={veri.id} odemeId={o.id} yenile={yenile} />
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Kart>

          <Dosyalar isEmri={veri} yenile={yenile} duzenlenebilir={duzenlenebilir} />

          {/* Bu iş emrine kaydedilen log analizleri. Kayıt vardı ama
              hiçbir ekranda görünmüyordu. */}
          <LogAnalizKarti isEmriId={veri.id} />
        </div>

        <div className="sutun" style={{ gap: 'var(--b4)' }}>
          <Kart>
            <div className="kart-govde sutun">
              <div className="satir-arasi">
                <span style={{ color: 'var(--metin-2)' }}>Tutar</span>
                <span className="para" style={{ fontSize: 'var(--p-17)', fontWeight: 600 }}>{para(veri.tutar)}</span>
              </div>
              <div className="satir-arasi">
                <span style={{ color: 'var(--metin-2)' }}>Tahsil edilen</span>
                <span className="para" style={{ color: 'var(--olumlu)' }}>{para(veri.tahsil_edilen)}</span>
              </div>
              <div className="satir-arasi" style={{ paddingTop: 'var(--b3)', borderTop: '1px solid var(--cizgi)' }}>
                <span style={{ fontWeight: 600 }}>Kalan</span>
                <span className="para" style={{ fontWeight: 600, color: kalan > 0 ? 'var(--uyari)' : 'var(--olumlu)' }}>
                  {para(kalan)}
                </span>
              </div>
            </div>
          </Kart>

          <Kart>
            <KartBaslik baslik="Bilgiler" />
            <div className="kart-govde">
              <dl className="tanim">
                <dt>Müşteri</dt>
                <dd><Link to={`/musteriler/${veri.musteri_id}`} style={{ textDecoration: 'underline' }}>{veri.musteri_ad}</Link></dd>
                <dt>Telefon</dt><dd>{veri.musteri_telefon || '—'}</dd>
                <dt>Plaka</dt><dd>{plakaGoster(veri.plaka) || ARACSIZ}</dd>
                <dt>Araç</dt><dd>{[veri.marka, veri.model].filter(Boolean).join(' ') || '—'}</dd>
                <dt>Motor</dt><dd>{veri.motor || '—'}</dd>
                <dt>Kilometre</dt><dd>{veri.kilometre ? veri.kilometre.toLocaleString('tr-TR') : '—'}</dd>
                <dt>Açılış</dt><dd>{tarihSaatGoster(veri.acilis_at)}</dd>
                {veri.kapanis_at && (<><dt>Kapanış</dt><dd>{tarihSaatGoster(veri.kapanis_at)}</dd></>)}
                <dt>Açan</dt><dd>{veri.olusturan_ad}</dd>
              </dl>
              {veri.islem && (
                <div style={{ marginTop: 'var(--b4)', paddingTop: 'var(--b4)', borderTop: '1px solid var(--cizgi)' }}>
                  <div style={{ fontSize: 'var(--p-12)', color: 'var(--metin-3)', marginBottom: 4 }}>Yapılacak işlem</div>
                  <div style={{ fontSize: 'var(--p-13)' }}>{veri.islem}</div>
                </div>
              )}
              {veri.not && (
                <div style={{ marginTop: 'var(--b4)', paddingTop: 'var(--b4)', borderTop: '1px solid var(--cizgi)' }}>
                  <div style={{ fontSize: 'var(--p-12)', color: 'var(--metin-3)', marginBottom: 4 }}>Not</div>
                  <div style={{ fontSize: 'var(--p-13)', whiteSpace: 'pre-wrap' }}>{veri.not}</div>
                </div>
              )}
            </div>
          </Kart>

          {yetkili('is_emri.sil') && (
            <button className="btn btn-tehlike btn-tam" onClick={() => setSilAcik(true)}>
              <Simge ad="cop" className="simge" /> İş emrini sil
            </button>
          )}
        </div>
      </div>

      {kalemAcik && <KalemPenceresi isEmri={veri} kapat={() => setKalemAcik(false)} yenile={yenile} />}
      {odemeAcik && <OdemePenceresi isEmri={veri} kalan={kalan} kapat={() => setOdemeAcik(false)} yenile={yenile} />}
      {duzenleAcik && <DuzenlePenceresi isEmri={veri} kapat={() => setDuzenleAcik(false)} yenile={yenile} />}
      {silAcik && (
        <OnayPenceresi
          baslik="İş emrini sil"
          aciklama={`${veri.no} numaralı iş emri çöpe taşınacak. Yönetici çöp kutusundan geri alabilir.`}
          kapat={() => setSilAcik(false)}
          onayla={() => void sil.yurut(undefined as never)}
          calisiyor={sil.calisiyor}
          hata={sil.hata}
        />
      )}
    </div>
  );
}

/* ---------- Kalem silme ---------- */

function KalemSil({ isEmriId, kalemId, yenile }: { isEmriId: number; kalemId: number; yenile: () => void }) {
  const bildir = useBildirim();
  const { yurut, calisiyor, hata } = useEylem(async () => {
    await api.sil(`/is-emirleri/${isEmriId}/kalemler/${kalemId}`);
    yenile();
    bildir('Kalem kaldırıldı, ürünse stok iade edildi');
  });
  /* Hata yutulursa kullanıcı düğmeye basıp hiçbir şey olmadığını görür ve
     nedenini öğrenemez; bu yüzden mesaj düğmenin yanında gösterilir. */
  return (
    <span className="satir" style={{ gap: 4 }}>
      {hata && (
        <span style={{ color: 'var(--kirmizi)', fontSize: 'var(--p-11)' }} title={hata}>
          Silinemedi
        </span>
      )}
      <button className="btn btn-sade btn-simge btn-kucuk" disabled={calisiyor}
        onClick={() => void yurut(undefined as never)} aria-label="Kalemi kaldır">
        <Simge ad="carpi" boyut={14} />
      </button>
    </span>
  );
}

/* ---------- Ödeme silme ---------- */

/**
 * Yanlış girilen ödemeyi iş emri ekranından kaldırır. Sunucudaki uç zaten
 * vardı ama arayüzde çağıran hiçbir yer yoktu: kullanıcı Kasa ekranını
 * bulup kaydı oradan silmek zorunda kalıyordu.
 */
function OdemeSil({ isEmriId, odemeId, yenile }: { isEmriId: number; odemeId: number; yenile: () => void }) {
  const bildir = useBildirim();
  const [onay, setOnay] = useState(false);
  const sil = useEylem(async () => {
    await api.sil(`/is-emirleri/${isEmriId}/odeme/${odemeId}`);
    yenile();
    setOnay(false);
    bildir('Ödeme kaldırıldı, iş emrinin ödeme durumu güncellendi');
  });
  return (
    <>
      <button className="btn btn-sade btn-simge btn-kucuk" onClick={() => setOnay(true)}
        aria-label="Ödemeyi kaldır">
        <Simge ad="cop" boyut={14} />
      </button>
      {onay && (
        <OnayPenceresi
          baslik="Ödeme kaldırılsın mı?"
          aciklama="Kasa kaydı çöp kutusuna taşınır ve iş emrinin ödenen/kalan tutarı yeniden hesaplanır."
          onayMetni="Kaldır"
          kapat={() => setOnay(false)}
          onayla={() => void sil.yurut(undefined as never)}
          calisiyor={sil.calisiyor}
          hata={sil.hata}
        />
      )}
    </>
  );
}

/* ---------- Kalem ekleme ---------- */

function KalemPenceresi({ isEmri, kapat, yenile }: { isEmri: IsEmriDetay; kapat: () => void; yenile: () => void }) {
  const bildir = useBildirim();
  const [tur, setTur] = useState<'hizmet' | 'urun'>('hizmet');
  const [hizmetId, setHizmetId] = useState('');
  const [urunId, setUrunId] = useState('');
  const [aciklama, setAciklama] = useState('');
  const [adet, setAdet] = useState('1');
  const [fiyat, setFiyat] = useState('');
  /* Kalem başına para birimi. Kur kalem yazıldığı anda donar: iş emrinin
     TL toplamı sonradan kur oynadı diye değişmez. */
  const [kalemBirimi, setKalemBirimi] = useState<ParaBirimi>(ANA_PARA_BIRIMI);

  const hizmetler = useVeri<Hizmet[]>('/hizmetler');
  const urunler = useVeri<Urun[]>('/urunler?pasif=0');

  const { yurut, calisiyor, hata } = useEylem(async () => {
    await api.gonder(`/is-emirleri/${isEmri.id}/kalemler`, {
      tur,
      hizmet_id: tur === 'hizmet' && hizmetId ? Number(hizmetId) : null,
      urun_id: tur === 'urun' ? Number(urunId) : null,
      aciklama: aciklama.trim(),
      adet: Number(adet) > 0 ? Number(adet) : 1,
      /* `birim_fiyat` KULLANICININ YAZDIĞI sayıdır; TL karşılığını sunucu
         hesaplar (bkz. shared/dogrulama.ts · paraBirimi). */
      birim_fiyat: Number(fiyat) || 0,
      para_birimi: kalemBirimi,
    });
    yenile();
    bildir('Kalem eklendi');
    kapat();
  });

  function hizmetSec(deger: string) {
    setHizmetId(deger);
    const h = hizmetler.veri?.find((x) => String(x.id) === deger);
    if (h) {
      setAciklama(h.ad);
      if (h.varsayilan_fiyat != null) setFiyat(String(h.varsayilan_fiyat));
    }
  }
  function urunSec(deger: string) {
    setUrunId(deger);
    const u = urunler.veri?.find((x) => String(x.id) === deger);
    if (u) { setAciklama(u.ad); setFiyat(String(u.satis_fiyat)); }
  }

  const gecerli = aciklama.trim() && (tur === 'hizmet' || urunId);

  return (
    <Pencere
      baslik="Kalem ekle"
      alt={tur === 'urun' ? 'Ürün kalemi eklenince stoktan otomatik düşer' : undefined}
      kapat={kapat}
      altBolum={
        <>
          <button className="btn btn-sade" onClick={kapat} disabled={calisiyor}>Vazgeç</button>
          <button className="btn btn-birincil" onClick={() => void yurut(undefined as never)} disabled={!gecerli || calisiyor}>
            {calisiyor ? 'Ekleniyor…' : 'Ekle'}
          </button>
        </>
      }
    >
      <div className="sutun">
        <Secim
          etiket="Tür" value={tur} onChange={(d) => setTur(d as 'hizmet' | 'urun')}
          secenekler={[{ deger: 'hizmet', ad: 'Hizmet' }, { deger: 'urun', ad: 'Ürün (stoktan)' }]}
        />
        {tur === 'hizmet' ? (
          <Secim
            etiket="Hizmet" value={hizmetId} onChange={hizmetSec} bosMetin="Listeden seçin veya elle yazın"
            secenekler={(hizmetler.veri ?? []).filter((h) => h.aktif).map((h) => ({ deger: String(h.id), ad: h.ad }))}
          />
        ) : (
          <Secim
            etiket="Ürün" value={urunId} onChange={urunSec} bosMetin="Ürün seçin"
            secenekler={(urunler.veri ?? []).map((u) => ({
              deger: String(u.id), ad: `${u.ad} — stok ${u.adet} ${u.birim}`,
            }))}
          />
        )}
        <Girdi etiket="Açıklama" value={aciklama} onChange={setAciklama} required duzen="urunAdi" />
        <div className="izgara izgara-2">
          <Girdi etiket="Adet" type="number" min={0.01} step="any" value={adet} onChange={setAdet} />
          <ParaGirdi
            etiket="Birim fiyat" deger={fiyat} onChange={setFiyat}
            paraBirimi={kalemBirimi} onParaBirimi={setKalemBirimi}
          />
        </div>
        {hata && <UyariSerit ton="kritik">{hata}</UyariSerit>}
      </div>
    </Pencere>
  );
}

/* ---------- Ödeme ---------- */

function OdemePenceresi({ isEmri, kalan, kapat, yenile }: {
  isEmri: IsEmriDetay; kalan: number; kapat: () => void; yenile: () => void;
}) {
  const bildir = useBildirim();
  const [tutar, setTutar] = useState(String(kalan || ''));
  const [tip, setTip] = useState<string>('Nakit');
  const [not, setNot] = useState('');
  /* Ödeme hangi parayla alındıysa o kaydedilir. "400 € aldım" diyen
     kullanıcı kasada da 400 € görür; TL karşılığı tahsilata sayılır.
     Varsayılan TL'dir çünkü kalan tutar TL cinsinden doldurulmuştur —
     € seçilip o TL rakamı bırakılırsa kullanıcı uyarılmadan 50 katı
     ödeme kaydeder; bu yüzden birim değişince tutar TEMİZLENİR. */
  const [odemeBirimi, setOdemeBirimi] = useState<ParaBirimi>(ANA_PARA_BIRIMI);

  function odemeBirimiDegistir(yeni: ParaBirimi) {
    setOdemeBirimi(yeni);
    /* Kalan tutar TL'dir; başka bir para birimine geçilince o rakam
       anlamını yitirir. Yanlış bir sayıyı bırakmaktansa alanı boşaltıp
       kullanıcıya yazdırmak dürüsttür. */
    setTutar(yeni === ANA_PARA_BIRIMI ? String(kalan || '') : '');
  }

  const { yurut, calisiyor, hata } = useEylem(async () => {
    await api.gonder(`/is-emirleri/${isEmri.id}/odeme`, {
      tutar: Number(tutar), para_birimi: odemeBirimi, odeme_tipi: tip, not,
    });
    yenile();
    bildir('Ödeme alındı, kasa hareketi oluşturuldu');
    kapat();
  });

  return (
    <Pencere
      baslik="Ödeme al"
      alt={`${isEmri.no} · Kalan ${para(kalan)}`}
      kapat={kapat}
      altBolum={
        <>
          <button className="btn btn-sade" onClick={kapat} disabled={calisiyor}>Vazgeç</button>
          <button className="btn btn-birincil" onClick={() => void yurut(undefined as never)}
            disabled={calisiyor || !(Number(tutar) > 0)}>
            {calisiyor ? 'Kaydediliyor…' : 'Ödemeyi kaydet'}
          </button>
        </>
      }
    >
      <div className="sutun">
        <ParaGirdi
          etiket="Tutar" deger={tutar} onChange={setTutar} autoFocus
          paraBirimi={odemeBirimi} onParaBirimi={odemeBirimiDegistir}
        />
        <Secim
          etiket="Ödeme yöntemi" value={tip} onChange={setTip}
          secenekler={ODEME_TIPLERI.map((t) => ({ deger: t, ad: t }))}
        />
        <Girdi etiket="Not" value={not} onChange={setNot} placeholder="İsteğe bağlı" duzen="cumle" />
        <UyariSerit ton="notr">Bu ödeme kasaya girdi olarak işlenir ve müşterinin cari bakiyesinden düşer.</UyariSerit>
        {hata && <UyariSerit ton="kritik">{hata}</UyariSerit>}
      </div>
    </Pencere>
  );
}

/* ---------- Düzenleme ---------- */

function DuzenlePenceresi({ isEmri, kapat, yenile }: { isEmri: IsEmriDetay; kapat: () => void; yenile: () => void }) {
  const bildir = useBildirim();
  const [islem, setIslem] = useState(isEmri.islem);
  const [not, setNot] = useState(isEmri.not);
  const [kilometre, setKilometre] = useState(isEmri.kilometre ? String(isEmri.kilometre) : '');
  const [tutar, setTutar] = useState(String(isEmri.tutar));

  const { yurut, calisiyor, hata } = useEylem(async () => {
    await api.guncelle(`/is-emirleri/${isEmri.id}`, {
      musteri_id: isEmri.musteri_id,
      arac_id: isEmri.arac_id,
      islem, not,
      kilometre: kilometre ? Number(kilometre) : null,
      tutar: Number(tutar) || 0,
      durum: isEmri.durum,
      odeme_durumu: isEmri.odeme_durumu,
      kalemler: [],
    });
    yenile();
    bildir('İş emri güncellendi');
    kapat();
  });

  return (
    <Pencere
      baslik="İş emrini düzenle"
      kapat={kapat}
      altBolum={
        <>
          <button className="btn btn-sade" onClick={kapat} disabled={calisiyor}>Vazgeç</button>
          <button className="btn btn-birincil" onClick={() => void yurut(undefined as never)} disabled={calisiyor}>
            {calisiyor ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </>
      }
    >
      <div className="sutun">
        <Girdi etiket="Yapılacak işlem" value={islem} onChange={setIslem} />
        <div className="izgara izgara-2">
          <Girdi etiket="Kilometre" type="number" value={kilometre} onChange={setKilometre} />
          <ParaGirdi
            etiket="Tutar" deger={tutar} onChange={setTutar}
            disabled={isEmri.kalemler.length > 0}
            ipucu={isEmri.kalemler.length > 0 ? 'Kalemler tutarı belirliyor' : '₺'}
          />
        </div>
        <MetinAlani etiket="Not" value={not} onChange={setNot} rows={4} duzen="cumle" />
        {hata && <UyariSerit ton="kritik">{hata}</UyariSerit>}
      </div>
    </Pencere>
  );
}

/* ---------- Dosyalar ---------- */

function Dosyalar({ isEmri, yenile, duzenlenebilir }: {
  isEmri: IsEmriDetay; yenile: () => void; duzenlenebilir: boolean;
}) {
  const bildir = useBildirim();
  const [yukleniyor, setYukleniyor] = useState(false);

  /**
   * TÜRE GÖRE İKİ AYRI YOL.
   *
   * `foto` ve `log` büyür ve birikir: bunlar sunucu diskine YAZILMAZ,
   * doğrudan Google Drive arşivine akar. Sunucu bu kuralı zaten kendisi
   * de uyguluyor (`POST /dosyalar` bu iki türü reddeder) — arayüz doğru
   * ucu çağırmasaydı ekran hata verirdi.
   *
   * `ecu` ve `diger` küçüktür ve iş emriyle birlikte anında açılması
   * gerekir: onlar eskisi gibi sunucuda kalır.
   *
   * Drive kapalıyken foto/log için SUNUCUYA GERİ DÜŞÜLMEZ; kullanıcı
   * net hatayı görür ve yeniden deneyebilir.
   */
  const ARSIVE_GIDER = new Set(['foto', 'log']);

  async function dosyaSec(e: React.ChangeEvent<HTMLInputElement>, tur: string) {
    const dosya = e.target.files?.[0];
    e.target.value = '';
    if (!dosya) return;
    setYukleniyor(true);
    try {
      const form = new FormData();
      form.append('tur', tur);
      form.append('is_emri_id', String(isEmri.id));
      form.append('file', dosya);
      if (ARSIVE_GIDER.has(tur)) {
        await buyukDosyayiArsivle<Dosya>(form);
        yenile();
        bildir(`${dosya.name} Google Drive arşivine yüklendi`);
      } else {
        await api.dosya<Dosya>('/dosyalar', form);
        yenile();
        bildir(`${dosya.name} yüklendi`);
      }
    } catch (err) {
      bildir(err instanceof Error ? err.message : 'Dosya yüklenemedi', 'hata');
    } finally {
      setYukleniyor(false);
    }
  }

  return (
    <Kart>
      <KartBaslik baslik="Dosyalar">
        {duzenlenebilir && (
          <div className="satir">
            {(['log', 'ecu', 'foto'] as const).map((t) => (
              <label key={t} className="btn btn-ikincil btn-kucuk" style={{ cursor: 'pointer' }}>
                <Simge ad={t === 'foto' ? 'foto' : 'dosya'} boyut={14} />
                {DOSYA_TUR_ADI[t]}
                <input
                  type="file" hidden disabled={yukleniyor}
                  accept={t === 'foto' ? 'image/*' : undefined}
                  onChange={(e) => void dosyaSec(e, t)}
                />
              </label>
            ))}
          </div>
        )}
      </KartBaslik>
      {isEmri.dosyalar.length === 0 ? (
        <div className="kart-govde" style={{ color: 'var(--metin-3)', fontSize: 'var(--p-13)' }}>
          Log, ECU/TCU dosyası veya fotoğraf ekleyebilirsiniz.
          {' '}Fotoğraf ve log Google Drive arşivine yüklenir; ECU/TCU dosyası
          sunucuda saklanır.
        </div>
      ) : (
        <div className="tablo-sarma">
          <table className="tablo">
            <thead><tr><th>Dosya</th><th>Tür</th><th className="sag">Boyut</th><th /></tr></thead>
            <tbody>
              {isEmri.dosyalar.map((d) => (
                <tr key={d.id}>
                  <td className="ana">{d.ad}</td>
                  <td><Rozet ton="notr" sade>{DOSYA_TUR_ADI[d.tur]}</Rozet></td>
                  <td className="sag ikincil">{boyutGoster(d.boyut)}</td>
                  <td className="dar">
                    {/* AYNI AYARLARLA YENİDEN AÇ.
                        Post Studio'da üretilen görselin ayarları dosyayla
                        birlikte saklanır; müşteri "şunu biraz düzeltelim"
                        dediğinde personel şablonu, kadrajı, metinleri ve
                        plaka stilini baştan kurmak zorunda kalmasın diye. */}
                    {d.post_ayar && (
                      <a
                        className="btn btn-sade btn-kucuk"
                        href={`/post-studio?is_emri=${isEmri.id}&dosya=${d.id}`}
                        title="Bu görselin ayarlarıyla Post Studio'yu aç"
                      >
                        <Simge ad="yenile" boyut={14} /> Aynı ayarlarla aç
                      </a>
                    )}
                    <a className="btn btn-sade btn-kucuk" href={`/api/dosyalar/${d.id}/indir`} download>
                      <Simge ad="indir" boyut={14} /> İndir
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Kart>
  );
}
